import React, { useState, useEffect, useRef } from 'react';
import './Projects.css';
import {
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaImages,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import projectImages from '../utils/projectImages';
import { resolveGallerySlug } from '../utils/projectGallerySlug';
import Gallery from './Gallery';
import News from './News';
import GigaProject from '../components/GigaProject';
import ProjectGallery from './ProjectGallery';
import { MobileScrollRail } from './MobileScrollRail';
import { fetchProjects } from '../firebase/firestoreService';
import studioLogo from '../assets/LOGO.png';
import curseCover from '../assets/Curse of the Nile/WhatsApp Image 2025-11-30 at 9.10.24 PM.jpeg';
import deadzoneCover from '../assets/Deadzone/WhatsApp Image 2025-11-30 at 9.07.07 PM.jpeg';
import skywarCover from '../assets/Skywar/WhatsApp Image 2025-11-30 at 9.08.03 PM.jpeg';
import italianCover from '../assets/Italian Hotel/WhatsApp Image 2025-11-30 at 9.11.20 PM.jpeg';
import fnafCover from '../assets/FNAF/WhatsApp Image 2025-11-30 at 8.59.41 PM.jpeg';
import midnightCover from '../assets/Midnight Manor/WhatsApp Image 2025-11-30 at 9.03.58 PM.jpeg';

function projectHasGallery(project) {
  const slug = resolveGallerySlug(project);
  const bundled = slug ? (projectImages[slug]?.length ?? 0) : 0;
  const remote = Array.isArray(project.images) ? project.images.filter(Boolean).length : 0;
  return bundled > 0 || remote > 0;
}

function toGalleryProject(project) {
  return {
    title: project.title,
    location: project.cityOrSeason || project.location || '',
    slug: resolveGallerySlug(project) || undefined,
    images: project.images,
  };
}

const caseStudies = [
  { img: curseCover, title: 'Curse of the Nile', location: 'Riyadh Season, KSA', slug: 'curse-of-the-nile' },
  { img: deadzoneCover, title: 'DeadZone Experience', location: 'Dammam Global City, KSA', slug: 'deadzone' },
  { img: skywarCover, title: 'SkyWar', location: 'Runway, KSA', slug: 'skywar' },
  { img: italianCover, title: "L'Albergo del Terrore", location: 'Riyadh Season, KSA', slug: 'italian-hotel' },
  { img: fnafCover, title: "Five Nights at Freddy's", location: 'Riyadh Season, KSA', slug: 'fnaf' },
  { img: midnightCover, title: 'Vampire Hotel', location: 'Jeddah Season, KSA', slug: 'midnight-manor' },
];

const Projects = () => {
  const [videoSrc, setVideoSrc] = useState(null);
  const [activeGallery, setActiveGallery] = useState(null);
  const [visibleElements, setVisibleElements] = useState(new Set());
  const [projectList, setProjectList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const scrollContainerRef = useRef(null);

  const observerRef = useRef();

  // Fetch projects from Firestore
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        const projects = await fetchProjects();

        // Transform Firestore data to match component structure
        const transformedProjects = projects.map((project) => {
          const row = {
            id: project.id,
            title: project.title,
            description: project.text,
            year: project.year || '',
            cityOrSeason: project.cityOrSeason || '',
            slug: project.slug || undefined,
            image: project.images && project.images.length > 0 ? project.images[0] : project.imageUrl,
            images: project.images || (project.imageUrl ? [project.imageUrl] : []),
            video: null,
            path: project.link,
            explore: project.link,
          };
          const gallerySlug = resolveGallerySlug(row);
          return {
            ...row,
            slug: gallerySlug ?? row.slug,
          };
        });

        setProjectList(transformedProjects);
      } catch (err) {
        console.error('Error loading projects:', err);
        setError('Failed to load projects. Please try again later.');

        // Fallback to static data if Firestore fails
        setProjectList([
          {
            id: '1',
            title: 'A lot Like Life',
            description: 'A realistic horror experience that mirrors everyday life',
            image: 'https://i.postimg.cc/R0gCSbSb/logo-1-01-1.png',
            images: ['https://i.postimg.cc/R0gCSbSb/logo-1-01-1.png'],
            video: null,
            path: 'https://kuula.co/post/5QJPx',
            explore: 'https://kuula.co/post/5QJPx'
          },
          {
            id: '2',
            title: 'mummy 2023',
            description: 'An immersive horror experience set',
            image: 'https://i.postimg.cc/52TGJBLW/mummy-quest-logo-curse-2.jpg',
            images: ['https://i.postimg.cc/52TGJBLW/mummy-quest-logo-curse-2.jpg'],
            video: null,
            path: 'https://kuula.co/share/collection/7JV5Q',
            explore: 'https://kuula.co/share/collection/7JV5Q'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    // Only observe elements after a short delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      elements.forEach(el => observerRef.current.observe(el));
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [projectList]); // Re-run when projectList changes

  const openVideo = (src) => setVideoSrc(src);
  const closeVideo = () => setVideoSrc(null);

  const scroll = (direction) => {
    const amount = 300;
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -amount : amount,
        behavior: 'smooth',
      });
    }
  };

  const nextImage = (projectId, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [projectId]: ((prev[projectId] || 0) + 1) % imagesLength
    }));
  };

  const prevImage = (projectId, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [projectId]: ((prev[projectId] || 0) - 1 + imagesLength) % imagesLength
    }));
  };

  return (
    <div className="projects-page">
      {/* Hero + Featured Projects (single section) */}
      <section className="projects-intro-section" aria-labelledby="projects-hero-title">
        <header className="projects-hero-block">
          <div className="hero-content">
            <div className={`hero-logo-mark animate-on-scroll ${visibleElements.has('projects-hero-logo') ? 'animate-fade-in-down' : ''}`} id="projects-hero-logo">
              <img src={studioLogo} alt="Studio 13" />
            </div>
            <h1 className={`animate-on-scroll ${visibleElements.has('projects-hero-title') ? 'animate-fade-in-down' : ''}`} id="projects-hero-title">Our Projects</h1>
            <p className={`hero-subtitle animate-on-scroll ${visibleElements.has('projects-hero-subtitle') ? 'animate-fade-in-up' : ''}`} id="projects-hero-subtitle">Immersive Horror Experiences & Interactive Entertainment</p>
          </div>
        </header>

        <div className="featured-projects-block">
          <div className="container">
            <div className={`section-header animate-on-scroll ${visibleElements.has('featured-header') ? 'animate-fade-in-up' : ''}`} id="featured-header">
              <span className="section-tag">Portfolio</span>
              <h2>Featured Projects</h2>
              <div className="section-divider"></div>
            </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading projects...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p className="error-message">{error}</p>
              <button
                className="retry-btn"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : projectList.length > 0 ? (
            <div className={`projects-container animate-on-scroll ${visibleElements.has('projects-grid') ? 'animate-fade-in-up' : ''}`} id="projects-grid">
              <button className="scroll-arrow scroll-left" onClick={() => scroll('left')}>
                <FaChevronLeft />
              </button>

              <div className="projects-cards" ref={scrollContainerRef}>
                {projectList.map((project) => {
                  const hasMultipleImages = project.images && project.images.length > 1;
                  const currentIdx = currentImageIndex[project.id] || 0;
                  const currentImage = hasMultipleImages ? project.images[currentIdx] : project.image;
                  const showViewGallery = projectHasGallery(project);
                  const openProjectGallery = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveGallery(toGalleryProject(project));
                  };

                  return (
                    <div key={project.id} className="project-card">
                      <div className="project-image-container">
                        <img src={currentImage} alt={project.title} className="project-image" />

                        {/* Image count badge */}
                        {hasMultipleImages && (
                          <div className="image-count-badge">
                            {currentIdx + 1}/{project.images.length}
                          </div>
                        )}

                        {/* Image navigation arrows */}
                        {hasMultipleImages && (
                          <>
                            <button
                              className="image-nav image-nav-prev"
                              onClick={(e) => {
                                e.stopPropagation();
                                prevImage(project.id, project.images.length);
                              }}
                            >
                              <FaChevronLeft />
                            </button>
                            <button
                              className="image-nav image-nav-next"
                              onClick={(e) => {
                                e.stopPropagation();
                                nextImage(project.id, project.images.length);
                              }}
                            >
                              <FaChevronRight />
                            </button>
                          </>
                        )}

                        <div className="project-overlay">
                          <div className="project-actions">
                            {project.video && (
                              <button className="action-btn video-btn" onClick={() => openVideo(project.video)}>
                                <span className="btn-icon">🔶</span>
                                Trailer
                              </button>
                            )}
                            {showViewGallery && (
                              <button
                                type="button"
                                className="action-btn gallery-btn"
                                onClick={openProjectGallery}
                              >
                                <FaImages className="btn-icon-inline" aria-hidden />
                                Gallery
                              </button>
                            )}
                            {project.path && (
                              <a href={project.path} target="_blank" rel="noopener noreferrer">
                                <button className="action-btn explore-btn">
                                  Explore
                                </button>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="project-info">
                        <h3>{project.title}</h3>

                        {/* Project metadata */}
                        <div className="project-metadata">
                          {project.year && (
                            <span className="metadata-badge year-badge">
                              <FaCalendarAlt className="metadata-icon" aria-hidden />
                              {project.year}
                            </span>
                          )}
                          {project.cityOrSeason && (
                            <span className="metadata-badge location-badge">
                              <FaMapMarkerAlt className="metadata-icon" aria-hidden />
                              {project.cityOrSeason}
                            </span>
                          )}
                        </div>

                        <p className="project-description">
                          {project.description || 'An immersive horror experience crafted by Studio 13'}
                        </p>

                        {showViewGallery && (
                          <button
                            type="button"
                            className="project-view-gallery"
                            onClick={openProjectGallery}
                          >
                            <FaImages className="project-view-gallery-icon" aria-hidden />
                            View gallery
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <MobileScrollRail scrollerRef={scrollContainerRef} />

              <button className="scroll-arrow scroll-right" onClick={() => scroll('right')}>
                <FaChevronRight />
              </button>
            </div>
          ) : (
            <div className="no-projects">
              <p>No projects available at the moment.</p>
            </div>
          )}
          </div>
        </div>
      </section>

      {/* Award Winning Attractions Section */}
      <section className="awards-section">
        <div className={`awards-wrapper animate-on-scroll ${visibleElements.has('awards') ? 'animate-fade-in-up' : ''}`} id="awards">
          <div className="awards-card">
            <div className="awards-grid">
              {caseStudies.map((cs) => (
                <div
                  key={cs.slug}
                  className="award-item"
                  onClick={() => setActiveGallery(cs)}
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${cs.title} gallery`}
                >
                  <div className="award-image">
                    <img src={cs.img} alt={cs.title} />
                    <div className="award-hover">
                      <span className="award-hover-text">View Gallery</span>
                    </div>
                  </div>
                  <p className="award-caption">Case Study: {cs.title}, {cs.location}</p>
                </div>
              ))}
            </div>
            <h2 className="awards-title">Award Winning Attractions</h2>
          </div>
          <div className="awards-logo">
            <img src={studioLogo} alt="Studio 13 Logo" />
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="news-section">
        <div className="container">
          <News />
        </div>
      </section>

      {/* Giga Project Section */}
      <section className="giga-section">
        <div className="container">
          <GigaProject />
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          <Gallery />
        </div>
      </section>

      {/* Video Lightbox */}
      {videoSrc && (
        <div className="video-lightbox" onClick={closeVideo}>
          <div className="video-popup" onClick={(e) => e.stopPropagation()}>
            <video controls autoPlay muted playsInline className="video-full">
              <source src={videoSrc} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <button className="close-video" onClick={closeVideo}>&times;</button>
          </div>
        </div>
      )}

      {/* Project Gallery Modal */}
      {activeGallery && (
        <ProjectGallery
          project={activeGallery}
          onClose={() => setActiveGallery(null)}
        />
      )}
    </div>
  );
};

export default Projects;
